import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ZaloLink from "@/components/feature/ZaloLink";
import { supabase } from "@/lib/supabase";
import { SUPPORT_PROGRAMS } from "@/pages/home/components/StatsSection";

interface SupportContent {
  slug: string;
  title_vi: string;
  title_kr: string;
  subtitle: string;
  content_html: string;
  icon: string;
  box_color: string;
  cover_image: string;
  display_order: number;
}

// Default content for each program
const DEFAULT_CONTENT: Record<string, Omit<SupportContent, "slug">> = {
  "ket-noi-doanh-nghiep": {
    title_vi: "Kết nối doanh nghiệp Hàn–Việt",
    title_kr: "비즈니스 매칭 지원",
    subtitle: "Tự động ghép cặp doanh nghiệp phù hợp theo ngành hàng và quy mô",
    content_html: `<h2>Dịch vụ Kết nối Doanh nghiệp</h2>
<p>Chúng tôi cung cấp dịch vụ kết nối doanh nghiệp Hàn Quốc và Việt Nam thông qua hệ thống ghép cặp thông minh dựa trên ngành hàng, quy mô và nhu cầu cụ thể của từng doanh nghiệp.</p>
<h3>Quy trình kết nối</h3>
<ul>
<li>Đăng ký hồ sơ doanh nghiệp và nhu cầu hợp tác</li>
<li>Hệ thống phân tích và đề xuất đối tác phù hợp</li>
<li>Kết nối trực tiếp qua nền tảng Bizwell</li>
<li>Hỗ trợ đàm phán và ký kết hợp đồng</li>
</ul>
<h3>Lợi ích</h3>
<p>Tiết kiệm thời gian tìm kiếm đối tác, giảm chi phí giao dịch và tăng tỷ lệ thành công trong hợp tác kinh doanh quốc tế.</p>`,
    icon: "ri-exchange-2-line",
    box_color: "#004A99",
    cover_image: "https://readdy.ai/api/search-image?query=Korean%20Vietnamese%20business%20meeting%20handshake%20partnership%20cooperation%20professional%20modern%20office%20boardroom%20two%20nations%20flag%20B2B%20connection&width=1200&height=500&seq=support_match_cover&orientation=landscape",
    display_order: 1,
  },
  "dich-vu-phien-dich": {
    title_vi: "Dịch vụ phiên & biên dịch",
    title_kr: "통번역 서비스",
    subtitle: "Hỗ trợ dịch thuật tài liệu kỹ thuật và hợp đồng Hàn–Việt",
    content_html: `<h2>Dịch vụ Phiên & Biên dịch Chuyên nghiệp</h2>
<p>Đội ngũ phiên dịch viên chuyên nghiệp của chúng tôi có kinh nghiệm sâu trong lĩnh vực kỹ thuật, thương mại và pháp lý Hàn–Việt.</p>
<h3>Các loại dịch vụ</h3>
<ul>
<li>Biên dịch tài liệu kỹ thuật và catalogue sản phẩm</li>
<li>Phiên dịch tại các cuộc họp và hội nghị</li>
<li>Dịch thuật hợp đồng thương mại và pháp lý</li>
<li>Hỗ trợ giao tiếp thời gian thực qua nền tảng số</li>
</ul>
<h3>Cam kết chất lượng</h3>
<p>Tất cả tài liệu được dịch bởi chuyên gia có chứng chỉ, đảm bảo độ chính xác và bảo mật thông tin tuyệt đối.</p>`,
    icon: "ri-translate-2",
    box_color: "#00AEEF",
    cover_image: "https://readdy.ai/api/search-image?query=professional%20interpreter%20translator%20Korean%20Vietnamese%20language%20document%20technical%20contract%20business%20communication%20modern%20clean%20office&width=1200&height=500&seq=support_translate_cover&orientation=landscape",
    display_order: 2,
  },
  "tu-van-thi-truong": {
    title_vi: "Tư vấn xâm nhập thị trường",
    title_kr: "시장 진출 컨설팅",
    subtitle: "Phân tích thị trường, chiến lược và hỗ trợ pháp lý xuất nhập khẩu",
    content_html: `<h2>Tư vấn Chiến lược Thị trường</h2>
<p>Chúng tôi cung cấp dịch vụ tư vấn toàn diện giúp doanh nghiệp Hàn Quốc xâm nhập thị trường Việt Nam một cách hiệu quả và bền vững.</p>
<h3>Nội dung tư vấn</h3>
<ul>
<li>Phân tích thị trường và đối thủ cạnh tranh</li>
<li>Chiến lược định giá và phân phối sản phẩm</li>
<li>Hỗ trợ thủ tục pháp lý xuất nhập khẩu</li>
<li>Tư vấn thuế và quy định thương mại</li>
<li>Xây dựng kế hoạch kinh doanh tại Việt Nam</li>
</ul>
<h3>Đội ngũ chuyên gia</h3>
<p>Chuyên gia tư vấn có hơn 10 năm kinh nghiệm trong lĩnh vực thương mại Hàn–Việt, am hiểu sâu về văn hóa kinh doanh và quy định pháp lý hai nước.</p>`,
    icon: "ri-bar-chart-grouped-line",
    box_color: "#004A99",
    cover_image: "https://readdy.ai/api/search-image?query=market%20analysis%20consulting%20strategy%20Vietnam%20Korea%20business%20growth%20chart%20data%20analytics%20professional%20modern%20office%20presentation&width=1200&height=500&seq=support_consult_cover&orientation=landscape",
    display_order: 3,
  },
  "ho-tro-trien-lam": {
    title_vi: "Hỗ trợ tham gia triển lãm",
    title_kr: "전시회 참가 지원",
    subtitle: "Kết nối gian hàng tại các hội chợ thương mại Hàn Quốc và Việt Nam",
    content_html: `<h2>Hỗ trợ Tham gia Triển lãm Thương mại</h2>
<p>Chúng tôi hỗ trợ doanh nghiệp tham gia các hội chợ và triển lãm thương mại lớn tại Hàn Quốc và Việt Nam, tạo cơ hội gặp gỡ đối tác trực tiếp.</p>
<h3>Dịch vụ hỗ trợ</h3>
<ul>
<li>Đăng ký và đặt gian hàng tại các triển lãm lớn</li>
<li>Thiết kế và trang trí gian hàng chuyên nghiệp</li>
<li>Hỗ trợ logistics và vận chuyển hàng mẫu</li>
<li>Phiên dịch tại chỗ trong suốt thời gian triển lãm</li>
<li>Kết nối với khách hàng tiềm năng trước và sau triển lãm</li>
</ul>
<h3>Các triển lãm tiêu biểu</h3>
<p>Vietnam Expo, Korea Trade Show, VIETBUILD, Seoul International Trade Fair và nhiều sự kiện thương mại quốc tế khác.</p>`,
    icon: "ri-store-2-line",
    box_color: "#00AEEF",
    cover_image: "https://readdy.ai/api/search-image?query=trade%20show%20exhibition%20booth%20Korean%20Vietnamese%20products%20display%20professional%20modern%20expo%20hall%20international%20business%20fair&width=1200&height=500&seq=support_expo_cover&orientation=landscape",
    display_order: 4,
  },
  "tim-kiem-doi-tac": {
    title_vi: "Tìm kiếm đối tác chiến lược",
    title_kr: "파트너 발굴 지원",
    subtitle: "Phân tích hồ sơ và giới thiệu đối tác phù hợp theo nhu cầu",
    content_html: `<h2>Dịch vụ Tìm kiếm Đối tác Chiến lược</h2>
<p>Chúng tôi sử dụng công nghệ AI và mạng lưới doanh nghiệp rộng lớn để tìm kiếm và giới thiệu đối tác chiến lược phù hợp nhất với nhu cầu của bạn.</p>
<h3>Quy trình tìm kiếm</h3>
<ul>
<li>Phân tích nhu cầu và tiêu chí đối tác của doanh nghiệp</li>
<li>Tìm kiếm trong cơ sở dữ liệu hơn 200 doanh nghiệp Hàn Quốc</li>
<li>Đánh giá và xếp hạng đối tác tiềm năng</li>
<li>Giới thiệu và kết nối trực tiếp</li>
<li>Theo dõi và hỗ trợ sau kết nối</li>
</ul>
<h3>Cam kết</h3>
<p>Chúng tôi cam kết giới thiệu tối thiểu 3 đối tác tiềm năng trong vòng 30 ngày làm việc, với tỷ lệ thành công hợp tác đạt trên 70%.</p>`,
    icon: "ri-search-eye-line",
    box_color: "#004A99",
    cover_image: "https://readdy.ai/api/search-image?query=strategic%20partner%20search%20network%20connection%20Korean%20Vietnamese%20business%20professional%20handshake%20agreement%20cooperation%20modern%20clean%20background&width=1200&height=500&seq=support_partner_cover&orientation=landscape",
    display_order: 5,
  },
};

const autoLinkContent = (html: string): string => {
  const parts = html.split(/(<[^>]+>)/g);
  return parts
    .map((part) => {
      if (part.startsWith("<") && part.endsWith(">")) return part;
      return part.replace(
        /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi,
        (url) => {
          const href = url.startsWith("http") ? url : `https://${url}`;
          return `<a href="${href}" target="_blank" rel="noopener noreferrer nofollow">${url}</a>`;
        }
      );
    })
    .join("");
};

const prepareHtml = (content: string): string => {
  if (!content) return "";
  let html = content;
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(content);
  if (!hasHtmlTags) {
    html = content
      .split(/\n\n+/)
      .map((para) => `<p>${para.replace(/\n/g, "<br>")}</p>`)
      .join("");
  }
  html = autoLinkContent(html);
  html = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "b", "strong", "i", "em", "u", "s", "strike",
      "h1", "h2", "h3", "h4", "h5", "h6",
      "ul", "ol", "li",
      "blockquote", "pre", "code",
      "a", "img", "span", "div",
      "table", "thead", "tbody", "tr", "th", "td", "hr",
    ],
    ALLOWED_ATTR: ["href", "src", "alt", "title", "target", "rel", "style", "class", "width", "height", "colspan", "rowspan"],
    ADD_ATTR: ["target"],
  });
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  doc.querySelectorAll("a").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer nofollow");
  });
  doc.querySelectorAll("img").forEach((img) => {
    img.style.maxWidth = "100%";
    img.style.borderRadius = "8px";
    img.style.height = "auto";
  });
  return doc.body.innerHTML;
};

const SupportPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [content, setContent] = useState<SupportContent | null>(null);
  const [loading, setLoading] = useState(true);

  const program = SUPPORT_PROGRAMS.find((p) => p.slug === slug);
  const defaultData = slug ? DEFAULT_CONTENT[slug] : null;

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    const fetchContent = async () => {
      setLoading(true);
      try {
        const { data } = await supabase
          .from("support_programs")
          .select("*")
          .eq("slug", slug)
          .maybeSingle();
        if (data) {
          setContent(data as SupportContent);
        } else {
          setContent(null);
        }
      } catch {
        setContent(null);
      }
      setLoading(false);
    };
    fetchContent();
  }, [slug]);

  const display = content ?? (defaultData ? { slug: slug ?? "", ...defaultData } : null);

  if (!program || !display) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-error-warning-line text-4xl text-gray-300 block mb-4" />
            <p className="text-gray-500">Không tìm thấy chương trình này</p>
            <Link to="/" className="mt-4 inline-flex items-center gap-2 text-[#004A99] font-medium text-sm cursor-pointer">
              <i className="ri-arrow-left-line" /> Về trang chủ
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative overflow-hidden" style={{ minHeight: 360 }}>
        <div className="absolute inset-0">
          <img
            src={display.cover_image || "https://readdy.ai/api/search-image?query=Korean%20Vietnamese%20business%20cooperation%20professional%20modern%20abstract%20background%20dark%20navy%20blue&width=1200&height=500&seq=support_default_cover&orientation=landscape"}
            alt={display.title_vi}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#001428]/90 via-[#001428]/70 to-[#001428]/40" />
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 py-16">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <Link to="/" className="text-white/60 hover:text-white text-sm cursor-pointer whitespace-nowrap">Trang chủ</Link>
            <i className="ri-arrow-right-s-line text-white/40 text-sm" />
            <span className="text-white/60 text-sm whitespace-nowrap">Chương trình Hỗ trợ</span>
            <i className="ri-arrow-right-s-line text-white/40 text-sm" />
            <span className="text-white text-sm whitespace-nowrap">{display.title_vi}</span>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${display.box_color}80` }}>
              <i className={`${display.icon} text-[#00AEEF] text-2xl`} />
            </div>
            <div>
              <p className="text-[#00AEEF] text-xs font-bold uppercase tracking-widest">{display.title_kr}</p>
            </div>
          </div>
          <h1 className="text-3xl lg:text-4xl font-black text-white mb-4 leading-tight max-w-2xl">
            {display.title_vi}
          </h1>
          <p className="text-white/70 text-base max-w-xl leading-relaxed">
            {display.subtitle}
          </p>
          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <Link
              to="/"
              className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white/10 px-6 py-3 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Về trang chủ
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-[1200px] mx-auto px-6 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Content */}
          <div className="lg:col-span-2">
            <style dangerouslySetInnerHTML={{ __html: `
              .support-content { font-size: 15px; line-height: 1.8; color: #374151; white-space: pre-wrap; }
              .support-content p { margin: 0.75em 0; min-height: 1.2em; }
              .support-content h1 { font-size: 1.75em; font-weight: 700; margin: 1em 0 0.5em; color: #111827; }
              .support-content h2 { font-size: 1.4em; font-weight: 700; margin: 0.9em 0 0.45em; color: #111827; }
              .support-content h3 { font-size: 1.2em; font-weight: 600; margin: 0.8em 0 0.4em; color: #1f2937; }
              .support-content b, .support-content strong { font-weight: 700; color: #111827; }
              .support-content i, .support-content em { font-style: italic; }
              .support-content u { text-decoration: underline; }
              .support-content ul { list-style-type: disc; padding-left: 1.5em; margin: 0.75em 0; }
              .support-content ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.75em 0; }
              .support-content li { margin: 0.35em 0; }
              .support-content blockquote { border-left: 4px solid #004A99; padding: 10px 16px; color: #4b5563; font-style: italic; margin: 1em 0; background: #f8f9fa; border-radius: 0 8px 8px 0; }
              .support-content a { color: #0066cc; text-decoration: underline; cursor: pointer; word-break: break-all; }
              .support-content a:hover { color: #004A99; }
              .support-content img { max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; }
              .support-content table { width: 100%; border-collapse: collapse; margin: 1em 0; font-size: 0.9em; }
              .support-content th, .support-content td { border: 1px solid #e5e7eb; padding: 8px 12px; text-align: left; }
              .support-content th { background: #f9fafb; font-weight: 600; }
              .support-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 1.5em 0; }
            ` }} />
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <i className="ri-loader-4-line animate-spin text-3xl text-[#004A99]" />
              </div>
            ) : (
              <div
                className="support-content"
                dangerouslySetInnerHTML={{ __html: prepareHtml(display.content_html) }}
              />
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            {/* CTA Card */}
            <div className="bg-[#001428] rounded-2xl p-6 text-white">
              <div className="w-10 h-10 rounded-xl bg-[#004A99]/50 flex items-center justify-center mb-4">
                <i className={`${display.icon} text-[#00AEEF] text-xl`} />
              </div>
              <h3 className="font-bold text-base mb-2">{display.title_vi}</h3>
              <p className="text-white/60 text-sm mb-5 leading-relaxed">{display.subtitle}</p>

            </div>

            {/* Other programs */}
            <div className="bg-[#F8F9FA] rounded-2xl p-5">
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-4">Chương trình khác</h4>
              <div className="space-y-2">
                {SUPPORT_PROGRAMS.filter((p) => p.slug !== slug).map((p) => (
                  <Link
                    key={p.slug}
                    to={`/support/${p.slug}`}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-white border border-transparent hover:border-gray-200 transition-all cursor-pointer group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#004A99]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#004A99] transition-colors">
                      <i className={`${p.icon} text-[#004A99] text-sm group-hover:text-white`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-700 group-hover:text-[#004A99] transition-colors truncate">{p.titleVi}</p>
                      <p className="text-[10px] text-gray-400 truncate">{p.titleKr}</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#004A99] text-sm flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className="bg-[#EDF2FB] rounded-2xl p-5 border border-[#004A99]/20">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-customer-service-2-line text-[#004A99] text-lg" />
                <h4 className="text-sm font-bold text-[#004A99]">Liên hệ tư vấn</h4>
              </div>
              <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                Cần hỗ trợ thêm? Đội ngũ chuyên gia của chúng tôi sẵn sàng tư vấn miễn phí.
              </p>
            <div className="flex justify-center">
              <ZaloLink
                className="inline-flex items-center justify-center gap-2 bg-[#0068FF] text-white text-xs font-semibold px-4 py-2 rounded-full hover:bg-[#0057d9] transition-all hover:scale-105 cursor-pointer whitespace-nowrap shadow-sm hover:shadow-md w-full max-w-[220px]"
                tooltipPosition="left"
                title="Gửi yêu cầu tư vấn qua Zalo"
              >
                <i className="ri-chat-3-line text-xs" />
                Gửi yêu cầu Zalo
              </ZaloLink>
            </div>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SupportPage;
